import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper,
  Alert,
  Chip,
  Stack,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function NewEtudiant() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirmPassword: '',
    formation: '',
    specialite: '',
    annee: '',
    description: '',
    competences: [],
    linkedin: '',
    github: '',
    photo: ''
  });

  const [newCompetence, setNewCompetence] = useState('');
  const [previewImage, setPreviewImage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCompetence = () => {
    if (newCompetence.trim()) {
      setFormData(prev => ({
        ...prev,
        competences: [...prev.competences, newCompetence.trim()]
      }));
      setNewCompetence('');
    }
  };

  const handleDeleteCompetence = (competenceToDelete) => {
    setFormData(prev => ({
      ...prev,
      competences: prev.competences.filter(comp => comp !== competenceToDelete)
    }));
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Vérifier la taille du fichier (5MB max)
    if (file.size > 5000000) {
      setError("L'image ne doit pas dépasser 5MB");
      return;
    }

    try {
      const formDataPhoto = new FormData();
      formDataPhoto.append('file', file);
      formDataPhoto.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET_STUDENT);

      const cloudinaryResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/upload`,
        {
          method: 'POST',
          body: formDataPhoto
        }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error("Erreur lors de l'upload de la photo");
      }

      const cloudinaryData = await cloudinaryResponse.json();
      const photoUrl = cloudinaryData.secure_url;

      setPreviewImage(photoUrl);
      setFormData(prev => ({
        ...prev,
        photo: photoUrl
      }));
      setError(''); // Effacer les erreurs précédentes
    } catch (error) {
      console.error('Erreur lors de l\'upload de la photo:', error);
      setError("Erreur lors de l'upload de la photo");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validations
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (!formData.photo) {
      setError('La photo est requise');
      setLoading(false);
      return;
    }

    try {
      const dataToSend = {
        user: {
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          password: formData.password,
          role: 'etudiant',
          role_id: 3  // Ajout du role_id pour étudiant
        },
        etudiant: {
          formation: formData.formation,
          specialite: formData.specialite,
          annee: formData.annee,
          description: formData.description,
          competences: formData.competences,
          linkedin: formData.linkedin || null,
          github: formData.github || null,
          photo: formData.photo
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/register`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erreur lors de la création de l'étudiant");
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/etudiants');
      }, 2000);

    } catch (err) {
      console.error("Erreur détaillée:", err);
      setError(err.message || "Une erreur est survenue lors de la création du compte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/etudiants')}
        variant="contained"
        color="primary"
        sx={{ mb: 4 }}
      >
        Retour aux étudiants
      </Button>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Ajouter un nouvel étudiant
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Étudiant créé avec succès! Redirection...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            required
            fullWidth
            label="Nom"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Prénom"
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Mot de passe"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Confirmer le mot de passe"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Formation"
            name="formation"
            value={formData.formation}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Spécialité"
            name="specialite"
            value={formData.specialite}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Année"
            name="annee"
            value={formData.annee}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Description"
            name="description"
            multiline
            rows={4}
            value={formData.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                label="Ajouter une compétence"
                value={newCompetence}
                onChange={(e) => setNewCompetence(e.target.value)}
              />
              <Button
                onClick={handleAddCompetence}
                variant="contained"
                color="primary"
              >
                Ajouter
              </Button>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {formData.competences.map((comp, index) => (
                <Chip
                  key={index}
                  label={comp}
                  onDelete={() => handleDeleteCompetence(comp)}
                  color="primary"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Stack>
          </Box>

          <TextField
            fullWidth
            label="LinkedIn URL"
            name="linkedin"
            value={formData.linkedin}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="GitHub URL"
            name="github"
            value={formData.github}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 3 }}>
            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
              color="primary"
              sx={{ mb: 2 }}
            >
              Choisir une photo
              <VisuallyHiddenInput 
                type="file" 
                accept="image/*"
                onChange={handlePhotoChange}
              />
            </Button>

            {previewImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Aperçu de la photo :
                </Typography>
                <Box
                  component="img"
                  src={previewImage}
                  alt="Aperçu"
                  sx={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'cover',
                    borderRadius: 1
                  }}
                />
              </Box>
            )}

            {!previewImage && (
              <Typography variant="subtitle2" color="error">
                * Une photo est requise
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/etudiants')}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              color="primary"
            >
              {loading ? 'Création...' : "Créer l'étudiant"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default NewEtudiant; 